<template>
  <div class="card">
    <div class="card-body" v-if="isInvestigation">
      <p class="card-text">
        Enable between 4 to 7 categories most relevant to this investigation from the list below (Honesty &amp; Integrity is included by default). Emphasize the
        categories that are the focus of the investigation, if needed. You may choose up to two.
      </p>
    </div>
    <div class="card-body">
      <ErrorSummary :error="error" />
      <form ref="form" class="needs-validation" :class="formWasValidated ? 'was-validated' : ''" novalidate @submit.prevent>
        <div class="form-group">
          <label>Select an interview type:</label>
          <select v-model="selectedTestId" class="form-control" :disabled="loadingMessage" required>
            <option value selected hidden disabled>Select an interview type</option>
            <option v-for="t in tests" v-bind:key="t.testId" v-bind:value="t.testId">
              {{ t.displayName }}
            </option>
          </select>
          <div class="invalid-feedback">Please select an interview type.</div>
        </div>
      </form>
    </div>
    <div class="card-body" v-if="selectedTestId">
      <div class="alert alert-info text-small" :class="showErrorAlert ? 'alert-danger' : 'alert-info'">
        <h6 class="alert-heading">
          <fa v-if="!showErrorAlert" icon="info-circle" />
          <fa v-if="showErrorAlert" icon="exclamation-circle" />
          Category and Emphasis selection
        </h6>
        <ul class="list-unstyled mb-0">
          <li><small>A minmum of five categories must be enabled, a maximum of eight.</small></li>
          <li><small>A maximum of two categories may be emphasized.</small></li>
        </ul>
      </div>
      <div class="table-responsive">
        <table class="table">
          <thead>
            <tr>
              <th scope="col" class="border-top-0">Category</th>
              <th class="text-center border-top-0 header-enabled">
                Enabled
                <span data-toggle="tooltip" data-placement="top" title="Enables or disables the category during the interview.">
                  <fa icon="question-circle" />
                </span>
                <br />
                ({{ enabledCount }})
              </th>
              <th scope="col" class="border-top-0 header-emphasized">
                Emphasized
                <span data-toggle="tooltip" data-placement="top" title="Emphasizes the category during the interview.">
                  <fa icon="question-circle" />
                </span>
                <br />
                ({{ emphasizedCount }})
              </th>
            </tr>
          </thead>
          <tbody>
            <tr colspan="12" v-if="!testCategories.length" class="text-muted text-center">
              <td colspan="12">
                No categories
              </td>
            </tr>
            <tr v-for="category in testCategories" :key="category.id">
              <td>
                <span class="pt-2" data-toggle="tooltip" data-placement="top" :title="category.description || 'N/A'">
                  {{ category.displayName }} <fa icon="question-circle" fixed-width />
                </span>
              </td>
              <td class="text-center">
                <div class="custom-control custom-switch">
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    :id="`${category.id}-enable`"
                    :checked="category.status === 'enabled'"
                    :disabled="loadingMessage || category.isRequired"
                    @change="toggleEnabled(category, $event)"
                  />
                  <label class="custom-control-label" :for="`${category.id}-enable`"></label>
                </div>
              </td>
              <td class="text-center">
                <div v-if="!category.hasEmphasis">
                  <abbr class="text-muted initialism" title="Emphasis is not available for this category.">n/a</abbr>
                </div>
                <div v-if="category.hasEmphasis" class="custom-control custom-switch">
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    :id="`${category.id}-emphasize`"
                    :checked="category.isEmphasized"
                    :disabled="loadingMessage || category.isRequired || category.status !== 'enabled'"
                    @change="toggleIsEmphasized(category, $event)"
                  />
                  <label class="custom-control-label" :for="`${category.id}-emphasize`"></label>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="card-footer">
      <form class="mt-3 needs-validation" :class="formWasValidated ? 'was-validated' : ''" novalidate @submit.prevent>
        <div class="row">
          <div class="col"></div>
          <div class="mr-3">
            <button type="button" class="btn btn-primary mr-2" @click="submit" :disabled="loadingMessage" @keyup.enter="submit">
              <span v-if="loadingMessage" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              {{ loadingMessage ? loadingMessage : "Save" }}
            </button>
            <button type="button" class="btn btn-secondary" @click="cancel" :disabled="loadingMessage" @keyup.enter="cancel">
              <span v-if="loadingMessage" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import $ from "jquery";
import ErrorSummary from "@/components/ErrorSummary.vue";

export default {
  components: {
    ErrorSummary,
  },
  props: {
    organizationId: String,
    interviewId: String,
  },
  data() {
    return {
      loadingMessage: null,
      formWasValidated: false,
      error: null,

      tests: [],
      testCategories: [],

      selectedTestId: null,
    };
  },
  created() {
    this.getTests();
  },
  mounted() {
    $('[data-toggle="tooltip"]').tooltip({ boundary: "window" });
  },
  watch: {
    interviewId() {
      this.reset();
    },
    selectedTestId() {
      if (this.selectedTestId) {
        this.getTestCategories();
      }
    },
  },
  computed: {
    ...mapGetters("user", ["user"]),
    showErrorAlert() {
      return this.testCategories.length && (this.showMaxCategoriesError || this.showMaxEmphasisError);
    },
    enabledCount() {
      return this.testCategories.filter(c => c.status === "enabled").length;
    },
    emphasizedCount() {
      return this.testCategories.filter(c => c.status === "enabled" && c.isEmphasized == true).length;
    },
    showMaxCategoriesError() {
      return this.enabledCount > 8;
    },
    showMaxEmphasisError() {
      return this.emphasizedCount > 2;
    },
    isInvestigation() {
      if (!this.tests) return false;
      var test = this.tests.find(t => t.testId == this.selectedTestId);
      return test && test.code == "investigations-v2021.12.06";
    },
  },
  methods: {
    getTests() {
      this.$axios
        .get(`/v1/organizations/${this.organizationId}/interviews/tests`)
        .then(res => (this.tests = res.data))
        .catch(() => this.$router.push("/404"));
    },
    getTestCategories() {
      this.$axios
        .get(`/v1/organizations/${this.organizationId}/users/${this.user.id}/tests/${this.selectedTestId}/categories`)
        .then(res => res.data)
        .then(items => items.map(i => ({ ...i, status: i.isRequired ? "enabled" : "disabled", isEmphasized: false })))
        .then(items => (this.testCategories = items))
        .catch(() => this.$router.push("/404"));
    },
    reset() {
      this.loadingMessage = false;
      this.formWasValidated = false;

      this.selectedTestId = null;
      this.testCategories = [];
    },
    cancel() {
      this.$emit("completed", { success: true });
    },
    submit() {
      // validations
      if (!this.$refs.form.checkValidity()) {
        this.formWasValidated = true;
        return;
      }

      const payload = {
        testId: this.selectedTestId,
        categories: this.testCategories
          .filter(c => c.status === "enabled")
          .map(c => {
            return {
              categoryId: c.id,
              isEmphasized: c.isEmphasized === true,
            };
          }),
      };

      this.loadingMessage = "Saving...";
      this.$axios
        .put(`/v1/organizations/${this.organizationId}/interviews/${this.interviewId}/attributes`, payload)
        .then(() => {
          this.$emit("completed", { success: true });
        })
        .catch(err => (this.error = err))
        .finally(() => (this.loadingMessage = null));
    },
    toggleIsEmphasized(item, $event) {
      if ($event.target.checked) {
        item.isEmphasized = true;
      } else {
        item.isEmphasized = false;
      }

      // force binding by updating the reference being enumerated
      this.testCategories = this.testCategories.slice();
    },
    toggleEnabled(item, $event) {
      if ($event.target.checked) {
        item.status = "enabled";
        item.isEmphasized = false;
      } else {
        item.status = "disabled";
        item.isEmphasized = false;
      }

      // force binding by updating the reference being enumerated
      this.testCategories = this.testCategories.slice();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";

.card-body {
  @extend .pb-1;
}

.table-responsive {
  height: 400px;
  overflow-y: scroll;

  table {
    margin-bottom: 0;

    thead tr:nth-child(1) th {
      background: white;
      position: sticky;
      top: 0;
      z-index: 10;
    }

    .header-enabled {
      width: 100px;
      @extend .text-center;
      @extend .pl-0;
    }

    .header-emphasized {
      width: 130px;
      @extend .text-center;
      @extend .pl-0;
    }
  }
}

.custom-control-label {
  cursor: pointer;
}
</style>
